/**
 * Firebase Auth error codes → sentences a traveller can act on.
 *
 * MIRRORED MODULE — the twin of `src/lib/auth-errors.ts` in
 * vhakacha-service-provider-app. The MESSAGES table must stay identical in
 * both repos so the two apps never explain the same failure differently. Edit
 * both or they drift.
 *
 * Deliberately vague on whether an account exists. `auth/user-not-found` and
 * `auth/wrong-password` get the same wording, because distinguishing them
 * turns the sign-in form into an account-enumeration oracle. Modern Firebase
 * projects already collapse both into `auth/invalid-credential` for this
 * reason; the older codes are mapped for projects with email enumeration
 * protection off.
 */
const MESSAGES = {
  // ── Sign in ──────────────────────────────────────────────
  'auth/invalid-credential': 'That email and password do not match an account.',
  'auth/wrong-password': 'That email and password do not match an account.',
  'auth/user-not-found': 'That email and password do not match an account.',
  'auth/invalid-email': 'That does not look like a valid email address.',
  'auth/user-disabled': 'This account has been disabled. Contact your administrator.',

  // ── Sign up ──────────────────────────────────────────────
  'auth/email-already-in-use': 'An account with this email already exists. Try signing in instead.',
  'auth/weak-password': 'Please choose a longer password — at least 6 characters.',
  'auth/operation-not-allowed': 'Email sign-in is not enabled for this project. Contact support.',

  // ── Rate limiting and connectivity ───────────────────────
  'auth/too-many-requests': 'Too many attempts. Wait a few minutes and try again.',
  'auth/network-request-failed': 'Could not reach the server. Check your connection and try again.',
  'auth/timeout': 'The request timed out. Please try again.',

  // ── Session ──────────────────────────────────────────────
  'auth/requires-recent-login': 'For security, please sign in again before making this change.',
  'auth/expired-action-code': 'That link has expired. Request a new one.',
  'auth/invalid-action-code': 'That link is not valid. It may already have been used.',
};

function codeOf(error) {
  if (error && typeof error === 'object' && typeof error.code === 'string') {
    return error.code;
  }
  return undefined;
}

/**
 * @param {unknown} error   Anything thrown by the Firebase Auth SDK.
 * @param {string} fallback Shown when the code is unknown or absent.
 * @returns {string}
 */
export function authErrorMessage(error, fallback = 'Something went wrong. Please try again.') {
  const code = codeOf(error);
  if (code && MESSAGES[code]) return MESSAGES[code];

  // An unmapped code is still more useful in the console than on screen —
  // showing raw SDK text to a traveller is the thing being fixed.
  if (code) console.error('Unmapped auth error code:', code, error);
  else console.error('Non-Firebase auth error:', error);

  return fallback;
}

export const __MESSAGES_FOR_TEST = MESSAGES;
